import { Component, type ErrorInfo, type ReactNode } from "react";

/*
 * The admin's render-error boundary (ux-admin-10), without JSX so
 * `scripts/lib/admin-error-boundary.test.mjs` can render it with Node. The
 * section and app fallbacks are in `section-error-boundary.tsx`.
 */

export interface ErrorBoundaryProps {
  children?: ReactNode;
  /** What to render instead of `children` once they threw. */
  fallback: (error: Error) => ReactNode;
  /** Log label ("[admin] overview section"). */
  label: string;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Catches a throw while rendering `children` and shows `fallback` instead,
 * so the rest of the tree stays mounted. Give it a `key` that changes with
 * what it wraps (the section) to start over with a fresh boundary.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    console.error(`${this.props.label} failed to render`, error, info.componentStack);
  }

  render(): ReactNode {
    return this.state.error ? this.props.fallback(this.state.error) : this.props.children;
  }
}
